import { useState, useEffect } from 'react';
import { Progress, Upload, UploadFile } from 'antd';
import type { UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { formatBytes, openNotification } from '../utils/helper';
import { REACT_APP_BASE_URL } from '../config';
import { ThrashIcon, UploadCloudIcon } from './icons';
import { FileIcon } from './icons';

interface DocumentuploadProps {
    useAuthorization?: boolean;
    title: string;
    uploaded?: boolean;
    runAfterUpload?: boolean;
    documentItemId?: number;
    documentUrl?: string;
    status?: number;
    onUpload: (url: string) => void;
    rejectedMessage?: string;
    returnURLOnly?: boolean;
    acceptText?: string;
    accept?: string;
    maxCount?: number;
    full?: boolean;
    small?: boolean;
}

export const DocumentUploader = ({
    useAuthorization,
    title,
    uploaded,
    documentUrl,
    status,
    rejectedMessage,
    acceptText,
    accept,
    maxCount,
    onUpload,
    small
}: DocumentuploadProps) => {
    const [percent, setPercent] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile<unknown>[]>([]);
    const [error, setError] = useState<string | null | undefined>(
        rejectedMessage
    );

    const beforeUpload = (file: RcFile) => {
        const isLesserThan150MB = file.size / 1024 / 1024 < 150;
        if (!isLesserThan150MB) {
            openNotification('error', 'File must be smaller than 150MB.');
        }
        return isLesserThan150MB;
    };

    const props: UploadProps = {
        maxCount: maxCount,
        disabled: status === 1,
        beforeUpload,
        accept: accept,
        showUploadList: false,
        name: 'file',
        action: `${REACT_APP_BASE_URL}/files/upload`,
        headers: useAuthorization
            ? {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            : undefined,
        onChange(info) {
            setPercent(info.file.percent || 0);
            setError(null);

            setUploading(info.file.status === 'uploading');

            if (info.file.status === 'done') {
                setFileList(info.fileList);
                onUpload(info.fileList[0].response.data?.fileUri);
                setUploading(false);
            } else if (info.file.status === 'error') {
                setFileList([]);
                setUploading(false);
                setError(`${info.file.name} file upload failed.`);
            }
        },
        method: 'POST',
        data: {
            fileType: 'OTHERS'
        }
    };

    useEffect(() => {
        if (uploaded) {
            let size: number = 0;
            let docName = 'file.png';

            if (documentUrl?.length) {
                docName = documentUrl?.split('?size=')[0];
                size = Number(documentUrl?.split('?size=')[1]);
            }

            setFileList([
                {
                    uid: 'rc-upload-1679585437822-5',
                    lastModified: 1679519998443,
                    name: title,
                    size: size,
                    type: 'image/png',
                    percent: 100,
                    status: 'done',
                    response: {
                        name: title,
                        status: 'done',
                        url: documentUrl,
                        thumbUrl: documentUrl
                    },
                    xhr: {}
                }
            ]);
        }
    }, []);

    return (
        <div className='lg:mb-4'>
            <label className='text-sm font-medium mb-[10px] text-steelBlue'>
                {title}
            </label>

            <div className='mt-2'>
                {fileList.length === 0 ? (
                    <Upload {...props}>
                        <div
                            className={`bg-white border border-appcolorfaintnine rounded-[12px] ${
                                small ? 'py-2' : 'py-6'
                            } px-4 w-full flex flex-col items-center text-center`}
                        >
                            <div className='w-full cursor-pointer'>
                                {uploading ? (
                                    <div className='flex'>
                                        <div className='pr-2'>
                                            <FileIcon />
                                        </div>

                                        <div className='text-left w-full'>
                                            <p className='text-steelBlue text-sm font-medium overflow-ellipsis truncate max-w-[200px]'>
                                                {fileList[0]?.name ??
                                                    'Uploading...'}
                                            </p>

                                            <div className='w-full'>
                                                <Progress
                                                    percent={Math.round(
                                                        percent
                                                    )}
                                                    showInfo={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className='w-full cursor-pointer'>
                                        <div className='w-[46px] mx-auto h-[46px] bg-aliceBlue rounded-[50%] flex items-center justify-center'>
                                            <div
                                                className={`${
                                                    small
                                                        ? 'w-[25px] h-[25px]'
                                                        : 'w-[40px] h-[40px]'
                                                } bg-athensGray rounded-[50%] flex items-center justify-center`}
                                            >
                                                <UploadCloudIcon />
                                            </div>
                                        </div>
                                        <p
                                            className={`text-appcolorprimary  font-semibold ${
                                                small
                                                    ? 'mt-2 text-xs'
                                                    : 'mt-4 text-sm'
                                            }`}
                                        >
                                            Click to upload
                                            <span className='pl-1 text-midnightHaze font-normal'>
                                                or drag and drop
                                            </span>
                                        </p>
                                        <p className='text-xs text-midnightHaze font-light pt-1'>
                                            {acceptText ??
                                                'Accepts .jpg, .png and .pdf files'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Upload>
                ) : (
                    <div
                        className={`bg-white rounded-[12px] p-4 w-full flex justify-between  border ${
                            error
                                ? 'border-appcolorred'
                                : 'border-appcolorprimary'
                        }`}
                    >
                        <div className='flex flex-1'>
                            <div className='pr-2'>
                                <FileIcon />
                            </div>

                            <div className='flex-1'>
                                <p className='text-steelBlue text-sm font-medium overflow-ellipsis truncate max-w-[200px]'>
                                    {fileList[0].name}
                                </p>
                                <p className='text-midnightHaze text-sm '>
                                    {formatBytes(fileList[0].size || 0)}
                                </p>
                            </div>
                        </div>

                        <div className=''>
                            {uploading ? (
                                <div className='flex'>
                                    <div className='pr-2'>
                                        <FileIcon />
                                    </div>

                                    <div className='text-left w-full flex-1'>
                                        <p className='text-steelBlue text-sm font-medium overflow-ellipsis truncate max-w-[200px]'>
                                            {fileList[0]?.name ||
                                                'Uploading...'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => {
                                        setFileList([]);
                                        onUpload('');
                                    }}
                                >
                                    <ThrashIcon />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {error !== null ? (
                <p className='mt-2 font-semibold text-sm text-appcolorred'>
                    {error}
                </p>
            ) : null}
        </div>
    );
};
